import {
  BROADCAST_LEAD_FIELDS,
  variableLabel,
  type BroadcastVariableBinding,
  type BroadcastVariableMapping,
} from "@school-sales-ai/shared";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * One row per `{{variable}}` in the template body.
 *
 * Labels come from the same `variableLabel` the preview uses, so a token reads
 * identically in both places.
 *
 * A lead field that turns out empty EXCLUDES that prospect rather than filling
 * in a fallback — the copy under the picker says so, because "7 quedan fuera"
 * is surprising if you were not told to expect it.
 */
export function BroadcastVariableMapper({
  variables,
  mapping,
  onChange,
}: {
  variables: string[];
  mapping: BroadcastVariableMapping;
  onChange: (mapping: BroadcastVariableMapping) => void;
}): React.JSX.Element | null {
  if (variables.length === 0) return null;

  function set(token: string, binding: BroadcastVariableBinding): void {
    onChange({ ...mapping, [token]: binding });
  }

  return (
    <div className="mt-4 space-y-3">
      <p className="text-sm font-semibold text-ink-900">Datos del mensaje</p>
      {variables.map((token) => {
        const binding = mapping[token];
        const source = binding?.source ?? "lead_field";
        return (
          <div key={token} className="flex flex-wrap items-center gap-2">
            <span className="w-40 shrink-0 text-sm text-ink-700">
              {/^\d+$/.test(token) ? `Dato ${token}` : variableLabel(token)}
            </span>

            <Select
              value={source}
              onValueChange={(value) => {
                set(
                  token,
                  value === "literal"
                    ? { source: "literal", value: "" }
                    : { source: "lead_field", field: "firstName" },
                );
              }}
            >
              <SelectTrigger className="w-[170px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="lead_field">Campo del prospecto</SelectItem>
                <SelectItem value="literal">Texto fijo</SelectItem>
              </SelectContent>
            </Select>

            {source === "literal" ? (
              <Input
                className="w-[220px]"
                placeholder="Lo mismo para todos"
                value={binding?.source === "literal" ? binding.value : ""}
                onChange={(e) => {
                  set(token, { source: "literal", value: e.target.value });
                }}
              />
            ) : (
              <Select
                value={binding?.source === "lead_field" ? binding.field : "firstName"}
                onValueChange={(value) => {
                  set(token, {
                    source: "lead_field",
                    field: value as (typeof BROADCAST_LEAD_FIELDS)[number]["key"],
                  });
                }}
              >
                <SelectTrigger className="w-[220px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BROADCAST_LEAD_FIELDS.map((f) => (
                    <SelectItem key={f.key} value={f.key}>
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        );
      })}
      <p className="text-xs text-ink-500">
        Si a un prospecto le falta el dato, queda fuera del envío — preferimos no
        escribirle antes que mandarle un mensaje que diga que no sabemos quién es.
      </p>
    </div>
  );
}
